import { DEFAULT_BRANDING, extractFormControlNames, LanguageService, Loader, ObjectPropertyNamesHolder, StoreLoader, UnsubscriberClass, ValidationService, ValidationServiceHelper } from '@aston/foundation';
import { FormGroup, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators, ReactiveFormsModule } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { PageHeaderComponent, LoaderSpinnerComponent, FormControlFeedbackComponent } from '@aston/foundation';
import { NgSelectComponent, NgOptionComponent } from '@ng-select/ng-select';
import { BehaviorSubject, Observable, Subject, of, timer } from 'rxjs';
import { Component, OnInit, computed, inject } from '@angular/core';
import { getCurrencySymbol, CommonModule } from '@angular/common';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgxCurrencyDirective } from 'ngx-currency';
import { RouterModule } from '@angular/router';
import { NgxMaskDirective } from 'ngx-mask';
import { Store } from '@ngrx/store';

import { FileUploadInstructionsComponent } from '../../../shared-module/components/file-upload-instructions/file-upload-instructions.component';
import { INewAccountCompanyValidator } from '../../../shared-module/enums/validators/new-account.validators';
import { AnnualAccountingDocumentCount, LegalIdentifierStatus } from '../../../subsidiaries-module/enums';
import { FileUploadComponent } from '../../../shared-module/components/file-upload/file-upload.component';
import { SubsidiariesService } from '../../../subsidiaries-module/services/subsidiaries.service';
import { InfrastructureService } from '../../../shared-module/services/infrastructure.service';
import * as NewAccountStoreSelectors from '../../../root-store/new-account-store/selectors';
import * as SubsidiariesStoreActions from '../../../root-store/subsidiaries-store/actions';
import * as NewAccountStoreActions from '../../../root-store/new-account-store/actions';
import { ConsoleDomainValidators } from '../../../shared-module/enums/validators';
import * as AppStoreSelectors from '../../../root-store/app-store/selectors';
import * as AppStoreActions from '../../../root-store/app-store/actions';
import { SalesConditionsDocument } from '../../../shared-module/models';
import { ISubsidiaryForm } from '../../../subsidiaries-module/models';
import * as RoutesDefinitions from '../../../routes-definitions';
import { AppConstants } from '../../../app.constants';
import { NewAccountStep } from '../../enums';


export const formControlNames = (name: keyof ISubsidiaryForm) => name;

export type FormValidatorFieldsType = INewAccountCompanyValidator['fields'];

@Component({
	selector: 'new-account-company-page',
	templateUrl: './new-account-company-page.component.html',
	imports: [
		PageHeaderComponent,
		TranslateModule,
		CommonModule,
		RouterModule,
		LoaderSpinnerComponent,
		ReactiveFormsModule,
		NgSelectComponent,
		FormControlFeedbackComponent,
		NgOptionComponent,
		NgxCurrencyDirective,
		NgxMaskDirective,
		NgbTooltipModule,
		FileUploadComponent,
		FileUploadInstructionsComponent,
	]
})
export class NewAccountCompanyPageComponent extends UnsubscriberClass implements OnInit {
	private store = inject(Store);
	private formBuilder = inject(UntypedFormBuilder);
	private validationService = inject(ValidationService);
	private subsidiariesService = inject(SubsidiariesService);
	private infrastructureService = inject(InfrastructureService);
	private languageService = inject(LanguageService);

	form: UntypedFormGroup;
	formControlNames: ObjectPropertyNamesHolder<ISubsidiaryForm>;

	salesConditionsDocument: SalesConditionsDocument;
	validationHelper: ValidationServiceHelper;
	rulesetFields: FormValidatorFieldsType;
	fileConfig = AppConstants.FILE_UPLOAD_CONFIGS.TENANT_LOGO;
	subsidiariesRoutePath = RoutesDefinitions.getSubsidiariesFullPath();

	annualAccountingDocumentCount = AnnualAccountingDocumentCount;
	annualAccountingDocumentCountKeys = Object.keys(AnnualAccountingDocumentCount);

	subsidiaryIsLoading$ = this.store.select(NewAccountStoreSelectors.selectSelectedSubsidiarySlice).pipe(
		map(entity => entity.isLoading));

	salesConditions$ = this.store.select(AppStoreSelectors.selectSalesConditionsDocument);
	subsidiaryFrom$ = this.store.select(NewAccountStoreSelectors.selectSubsidiaryForm);
	subsidiaryFormLoader = new StoreLoader(
		this.store.select(NewAccountStoreSelectors.selectSubsidiaryFormIsLoading),
		this.store.select(NewAccountStoreSelectors.selectSubsidiaryFormError)
	);

	subsidiaryFromLegalIdentifier$ = this.store.select(NewAccountStoreSelectors.selectSubsidiaryFromLegalIdentifier);
	subsidiaryFromLegalIdentifierLoader = new StoreLoader(
		this.store.select(NewAccountStoreSelectors.selectSubsidiaryFromLegalIdentifierIsLoading),
		this.store.select(NewAccountStoreSelectors.selectSubsidiaryFromLegalIdentifierError)
	);

	subsidiaryBlockedFormLoader = new StoreLoader(
		this.store.select(NewAccountStoreSelectors.selectSubsidiaryBlockedFormIsLoading),
		this.store.select(NewAccountStoreSelectors.selectSubsidiaryBlockedFormError)
	);

	currentStep$ = this.store.select(NewAccountStoreSelectors.selectCurrentStep);

	currencySymbol$ = new BehaviorSubject('');

	currencies$ = this.languageService.language$.pipe(
		switchMap(lang => this.store.select(AppStoreSelectors.selectCurrencies(lang))),
		map(currencies => currencies.map(c => ({ ...c,  currencyName: `${c.currencyCode} - ${c.currencyName}` })).sort(function(a,b){
			return a.currencyName.localeCompare(b.currencyName);
		})),
	);

	countries$ = this.languageService.language$.pipe(
		switchMap(lang => this.store.select(AppStoreSelectors.selectCountries(lang))));

	currentUserIsSupport = this.store.selectSignal(AppStoreSelectors.selectCurrentUserIsSupport);

	fetchDependencies$ = new Subject<void>()
	fetchDependenciesAsSupport$ = this.fetchDependencies$.pipe(switchMap(_ => this.store.select(AppStoreSelectors.selectCurrentUserIsSupport)), filter(Boolean))

	timezones = toSignal(this.fetchDependenciesAsSupport$.pipe(switchMap(_ => this.infrastructureService.fetchTimezones())), { initialValue: []});
	databases = toSignal(this.fetchDependenciesAsSupport$.pipe(switchMap(_ => this.infrastructureService.fetchDatabases())), { initialValue: []});
	brandings = toSignal(this.fetchDependenciesAsSupport$.pipe(switchMap(_ => this.infrastructureService.fetchBranding())), { initialValue: []});

	databaseBuiltins = [{ databaseId: null, name: 'Automatic', forceNewCatalog: false }, { databaseId: null, name: 'New database', forceNewCatalog: true }]
	databaseOptions = computed(() => this.databaseBuiltins.concat(this.databases().map(db => ({ databaseId: db.id, name: `Use ${db.name}`, forceNewCatalog: false }))));

	timezoneBuiltins = ['Automatic']
	timezoneOptions = computed(() => this.timezoneBuiltins.concat(this.timezones()));

	ngOnInit() {
		// Load other subsidiaries for later use
		// User Story 20549: 🎮[Onboarding] Limiter la création d'une plateforme en autonomie: Création limitée à 5 entreprises par utilisateur
		this.store.dispatch(SubsidiariesStoreActions.LoadSubsidiariesRequest());
		this.form = this.formBuilder.group({});
		this.form.addControl(formControlNames('legalIdentifier'), new UntypedFormControl());
		this.form.addControl(formControlNames('name'), new UntypedFormControl());
		this.form.addControl(formControlNames('zipCode'), new UntypedFormControl());
		this.form.addControl(formControlNames('street'), new UntypedFormControl());
		this.form.addControl(formControlNames('city'), new UntypedFormControl());
		this.form.addControl(formControlNames('countryCode'), new UntypedFormControl());
		this.form.addControl(formControlNames('annualFigure'), new UntypedFormControl());
		this.form.addControl(formControlNames('annualAccountingDocumentCount'), new UntypedFormControl());
		this.form.addControl(formControlNames('currencyCode'), new UntypedFormControl());
		this.form.addControl(formControlNames('logo'), this.createFileUploaderFormGroup());
		this.form.addControl(formControlNames('salesConditionsAccepted'), new UntypedFormControl(false));
		this.form.addControl('databaseTarget', new UntypedFormControl(this.databaseBuiltins[0]));
		this.form.addControl('timezone', new UntypedFormControl(this.timezoneBuiltins[0]));
		this.form.addControl('brandingCode', new UntypedFormControl(DEFAULT_BRANDING));

		this.formControlNames = extractFormControlNames<ISubsidiaryForm>(this.form);

		this.validationService.getHelper(ConsoleDomainValidators.NewAccountCompanyValidator)
			.pipe(tap(([helper, fields]) => {
				this.validationHelper = helper;
				this.rulesetFields = fields as FormValidatorFieldsType;
				this.setFormValidators();
			}))
			.pipe(takeUntil(this.destroySubscriptions$))
			.subscribe();

		this.countryCodeField.valueChanges
			.pipe(
				debounceTime(200),
				distinctUntilChanged(),
				tap(_ => this.manageLegalIdentifierOnCountryCodeChange()),
				takeUntil(this.destroySubscriptions$)
			)
			.subscribe();

		this.legalIdentifierField.valueChanges
			.pipe(
				debounceTime(200),
				distinctUntilChanged(),
				tap(_ => this.clearLegalIdentifierIfFrance()),
				takeUntil(this.destroySubscriptions$)
			)
			.subscribe();

		this.currencyCodeField.valueChanges.pipe(
			map(code => getCurrencySymbol(code, 'narrow')),
			tap(symbol => this.currencySymbol$.next(symbol)),
			takeUntil(this.destroySubscriptions$)
		).subscribe();

		// Fill subsidiary info
		this.subsidiaryFromLegalIdentifier$.pipe(
			filter(subsidiary => !!subsidiary),
			filter(_ => this.isFrance),
			takeUntil(this.destroySubscriptions$)
		).subscribe(subsidiary => {

			this.form.patchValue({
				name: subsidiary.name,
				zipCode: subsidiary.zipCode,
				street: subsidiary.street,
				city: subsidiary.city
			});
		});

		// Retrieve form model when we are back from another step
		this.currentStep$.pipe(
			filter(step => step !== NewAccountStep.Company),
			switchMap(_ => this.subsidiaryFrom$),
			takeUntil(this.destroySubscriptions$)
		).subscribe(subsidiary => {
			this.form.patchValue(subsidiary);
		});

		// Sales conditions should be accpeted for non admins only
		this.store.select(AppStoreSelectors.selectCurrentUserIsSupport)
			.pipe(takeUntil(this.destroySubscriptions$))
			.subscribe(isSupport => {
				this.salesConditionsAcceptedField.clearValidators();
				if (!isSupport) this.salesConditionsAcceptedField.setValidators([Validators.requiredTrue]);
				this.salesConditionsAcceptedField.updateValueAndValidity()
			})

		// Load sales conditions document ID
		this.salesConditions$
			.pipe(filter<SalesConditionsDocument>(Boolean),takeUntil(this.destroySubscriptions$))
			.subscribe(entity => this.salesConditionsDocument = {
				...entity,
				id: entity.documentId
			});
		
		this.fetchDependencies$.next()
	}

	private manageLegalIdentifierOnCountryCodeChange() {
		// France : the information comes from the legal api
		this.clearLegalIdentifierIfFrance();

		this.form.patchValue({
			legalIdentifier: this.legalIdentifierField.value
		});

		// disable / enable address fields
		this.toggleLegalIdentifierRelatedFields(!this.isFrance);

		if (this.isFrance) {
			this.legalIdentifierField.setValidators([Validators.minLength(14)])
			this.legalIdentifierField.updateValueAndValidity()
		} else {
			this.legalIdentifierField.setValidators([Validators.maxLength(150)])
			this.legalIdentifierField.updateValueAndValidity()
		}
	}

	createFileUploaderFormGroup() {
		return this.formBuilder.group({
			fileInfo: this.formBuilder.control(null),
			underlyingFile: this.formBuilder.control(null),
			fileLoader: this.formBuilder.control(new Loader())
		});
	}

	setFormValidators() {
		this.validationHelper.control(this.legalIdentifierField, this.rulesetFields.LegalIdentifier);
		this.legalIdentifierField.setAsyncValidators([this.legalIdentifierValidator.bind(this)]);

		this.validationHelper.control(this.nameField, this.rulesetFields.Name);
		this.validationHelper.control(this.zipCodeField, this.rulesetFields.ZipCode);
		this.validationHelper.control(this.streetField, this.rulesetFields.Street);
		this.validationHelper.control(this.cityField, this.rulesetFields.City);
		this.validationHelper.control(this.countryCodeField, this.rulesetFields.Country);
		this.validationHelper.control(this.currencyCodeField, this.rulesetFields.CurrencyCode);

		this.validationHelper.control(this.annualFigureField, this.rulesetFields.AnnualFigure);
		this.validationHelper.control(this.annualAccountingDocumentCountField, this.rulesetFields.AnnualAccountingDocumentCount);
	}

	get legalIdentifierField() {
		return this.form.get(formControlNames('legalIdentifier'));
	}

	get nameField() {
		return this.form.get(formControlNames('name'));
	}

	get zipCodeField() {
		return this.form.get(formControlNames('zipCode'));
	}

	get streetField() {
		return this.form.get(formControlNames('street'));
	}

	get cityField() {
		return this.form.get(formControlNames('city'));
	}

	get countryCodeField() {
		return this.form.get(formControlNames('countryCode'));
	}

	get currencyCodeField() {
		return this.form.get(formControlNames('currencyCode'));
	}

	get annualFigureField() {
		return this.form.get(formControlNames('annualFigure'));
	}

	get annualAccountingDocumentCountField() {
		return this.form.get(formControlNames('annualAccountingDocumentCount'));
	}

	get logoField(): FormGroup {
		return this.form.get(formControlNames('logo')) as FormGroup;
	}

	get salesConditionsAcceptedField() {
		return this.form.get(formControlNames('salesConditionsAccepted'));
	}

	get isFrance() {
		const arrayFranceCountryCode = ["FR", "GF", "GP", "MQ", "YT", "RE", "BL", "MF", "PM"];
		return arrayFranceCountryCode.includes(this.countryCodeField.value);
	}

	get legalIdentifierMask() {
		return this.isFrance ? AppConstants.SIRET_MASK : null;
	}

	openSalesConditions(event: Event) {
		event.preventDefault();
		this.store.dispatch(AppStoreActions.PreviewDocument({
			document: this.salesConditionsDocument,
			title: 'PageTitles.SalesConditions'
		}))
	}

	legalIdentifierValidator(control: UntypedFormControl): Observable<ValidationErrors|null> {

		if (!this.isFrance) {
			return of(null);
		}

		return timer(200).pipe(
			map(_ => control.value),
			filter(value => value && value.length > 1),
			tap(_ => this.store.dispatch(NewAccountStoreActions.LoadSubsidiaryFromLegalIdentifierRequest())),
			switchMap(value => this.subsidiariesService.getSubsidiaryFromLegalIdentifier(value)),
			map(subsidiary => {
				this.store.dispatch(NewAccountStoreActions.UpdateSubsidiaryFromLegalIdentifierRequest(subsidiary));
				return subsidiary.legalIdentifierValidationStatus === LegalIdentifierStatus.Success ? null : {invalidIdentifier: true};
			}),
			catchError(error => {
				this.store.dispatch(NewAccountStoreActions.UpdateSubsidiaryFromLegalIdentifierFailure({error}));
				return of({wrongIdentifier: true});
			}),
			take(1),
		);
	}


	clearLegalIdentifierIfFrance() {
		// France : the information comes from the legal api
		if(this.isFrance){
			this.form.patchValue({
				name: null,
				zipCode:null,
				street: null,
				city: null
			});
		}
	}

	toggleLegalIdentifierRelatedFields(enable = true) {
		if (enable) {
			this.nameField.enable();
			this.zipCodeField.enable();
			this.streetField.enable();
			this.cityField.enable();
			this.countryCodeField.enable();
		} else {
			this.nameField.disable();
			this.zipCodeField.disable();
			this.streetField.disable();
			this.cityField.disable();
		}
	}

	getModelFromForm(): ISubsidiaryForm {
		return {
			legalIdentifier: this.legalIdentifierField.value,
			name: this.nameField.value,
			logo: this.logoField.value,
			street: this.streetField.value,
			zipCode: this.zipCodeField.value,
			city: this.cityField.value,
			countryCode: this.countryCodeField.value,
			currencyCode: this.currencyCodeField.value,
			annualFigure: this.annualFigureField.value,
			annualAccountingDocumentCount: this.annualAccountingDocumentCountField.value,
			salesConditionsAccepted: !!this.salesConditionsAcceptedField.value,
			cgvDocumentId: this.salesConditionsAcceptedField.value ? this.salesConditionsDocument.documentId : null,
			timeZone: this.currentUserIsSupport() && this.form.get('timezone').value !== 'Automatic' ? this.form.get('timezone').value : null,
			databaseId: this.currentUserIsSupport() ? this.form.get('databaseTarget').value?.databaseId : null,
			forceNewCatalog: this.currentUserIsSupport() ? this.form.get('databaseTarget').value?.forceNewCatalog : false,
			brandingCode: (this.currentUserIsSupport() ? this.form.get('brandingCode').value : null) || DEFAULT_BRANDING,
		};
	}

	onSubmit() {
		this.store.dispatch(NewAccountStoreActions.SubmitSubsidiaryFormRequest(this.getModelFromForm()));
	}

	onCancel() {
		this.store.dispatch(NewAccountStoreActions.CancelCreation());
	}
}
